/**
 * A lista de reordenação dos passos de um frame.
 *
 * ⚠️ **Ela é SLOT de um `editor-menu-button`**, e não mais o conteúdo de um popper próprio.
 * A troca veio do 1º ciclo de campo: no iPad do operador a lista não abria, porque o popper
 * default renderiza `position: absolute` **dentro do shadow DOM da barra** e ali qualquer
 * ancestral pode cortá-lo. O `editor-menu-button` usa `topLayer: true` — Popover API nativa,
 * fora de todo clipping — e é o mesmo mecanismo do ⋮ que funciona no aparelho dele.
 *
 * ═══ Por que isto é ADAPTAÇÃO e não obra nova ══════════════════════════════════════════════
 *
 * `present/frame-order-menu.ts` já resolve, para FRAMES, exatamente o problema que temos para
 * ELEMENTOS: uma lista arrastável que grava um índice fracionário entre os dois vizinhos do
 * destino. A matemática do arraste (o clone que segue o ponteiro, a linha indicadora, o
 * cálculo de `newIndex` a partir do `pageY`) é código provado em produção e **foi copiada
 * quase verbatim** — o que muda é a fonte de dados e o que se grava.
 *
 * | | `frame-order-menu` (upstream) | aqui |
 * |---|---|---|
 * | fonte | `frameMgr.frames` | os passos do frame atual |
 * | grava | `presentationIndex` do frame | `presentationSteps[elementId]` do frame |
 * | rótulo | título do frame | tipo do elemento + número do passo |
 *
 * Reusar a mecânica em vez de reinventá-la é a Regra #15, e aqui ela tem um motivo extra: o
 * arraste é justamente a parte que não se testa fora do navegador. Copiar o que já funciona é
 * o caminho de menor risco.
 *
 * ⚠️ **Empate não se cria arrastando.** `generateKeyBetweenV2(antes, depois)` sempre devolve
 * uma chave ESTRITAMENTE entre as duas, então arrastar produz ordem total. O empate (§9.5 do
 * dossiê) é permitido pelo MODELO — dois elementos que já tenham o mesmo índice revelam
 * juntos, e a lista os mostra com o mesmo número. Não há ação de agrupar nesta versão, e é
 * proposital: permitir o empate agora custa zero, e proibi-lo hoje seria migração de dado
 * amanhã.
 */

import { EdgelessCRUDIdentifier } from '@blocksuite/affine-block-surface';
import type { FrameBlockModel } from '@blocksuite/affine-model';
import { DisposableGroup } from '@blocksuite/global/disposable';
import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import type { BlockComponent } from '@blocksuite/std';
import { GfxControllerIdentifier } from '@blocksuite/std/gfx';
import { css, html, LitElement, nothing } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

import { desmarcarElemento, moverPasso, passosDoFrame } from './marcacao';
import { rotuloDoElemento } from './rotulo';

export const TAG_DO_MENU_DE_PASSOS = 'tuninho-menu-de-passos';

export class TuninhoMenuDePassos extends SignalWatcher(
  WithDisposable(LitElement)
) {
  static override styles = css`
    :host {
      display: block;
    }
    /*
     * Sem fundo, sem sombra e sem borda: a lista é SLOT de um editor-menu-button, e quem
     * desenha o painel é o editor-menu-content dele. Repetir a moldura aqui produziria duas
     * molduras — e a de dentro fora do lugar.
     *
     * (Sem crase neste comentário de propósito: ele vive DENTRO de um template literal css,
     * e uma crase aqui fecha o literal. Ver o gate em test_passos_de_apresentacao.py.)
     */
    .container {
      max-height: 281px;
      min-width: 240px;
      overflow: auto;
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
    .vazio {
      font-size: 12px;
      line-height: 18px;
      color: var(--affine-text-secondary-color);
      padding: 4px 6px;
      max-width: 240px;
    }
    .item {
      box-sizing: border-box;
      width: 240px;
      border-radius: 4px;
      padding: 4px;
      display: flex;
      gap: 6px;
      align-items: center;
      cursor: grab;
    }
    .draggable:hover {
      background-color: var(--affine-hover-color);
    }
    .item:hover .drag-indicator {
      opacity: 1;
    }
    .drag-indicator {
      cursor: pointer;
      width: 4px;
      height: 12px;
      border-radius: 1px;
      opacity: 0.2;
      background: var(--affine-placeholder-color);
      margin-right: 2px;
    }
    .numero {
      flex: 0 0 auto;
      min-width: 18px;
      height: 18px;
      line-height: 18px;
      text-align: center;
      border-radius: 9px;
      font-size: 11px;
      font-weight: 600;
      color: var(--affine-white);
      background: var(--affine-primary-color);
    }
    .titulo {
      flex: 1 1 auto;
      font-size: 14px;
      line-height: 22px;
      color: var(--affine-text-primary-color);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .tirar {
      flex: 0 0 auto;
      border: none;
      background: transparent;
      color: var(--affine-text-secondary-color);
      cursor: pointer;
      font-size: 14px;
      line-height: 18px;
      padding: 0 4px;
    }
    .tirar:hover {
      color: var(--affine-error-color, #eb4335);
    }
    .clone {
      visibility: hidden;
      position: absolute;
      z-index: 1;
      left: 8px;
      height: 30px;
      border: 1px solid var(--affine-border-color);
      box-shadow: var(--affine-menu-shadow);
      background-color: var(--affine-white);
      pointer-events: none;
    }
    .indicator-line {
      visibility: hidden;
      position: absolute;
      z-index: 1;
      left: 8px;
      background-color: var(--affine-primary-color);
      height: 1px;
      width: 90%;
    }
  `;

  get crud() {
    return this.edgeless.std.get(EdgelessCRUDIdentifier);
  }

  private get _gfx() {
    return this.edgeless.std.get(GfxControllerIdentifier);
  }

  private get _passos() {
    return this.frame ? passosDoFrame(this.frame) : [];
  }

  /**
   * A mecânica de arraste, copiada de `frame-order-menu.ts:117-204`.
   *
   * Preservada quase verbatim de propósito: é a parte que só o navegador prova, e trocar
   * matemática provada por matemática nova aqui seria risco sem ganho. O que muda é o bloco
   * final — em vez de `crud.updateElement(frame.id, { presentationIndex })`, chama
   * `moverPasso`, que decide o índice em `passos.ts` (puro e testado).
   */
  private _bindEvent() {
    const { _disposables } = this;

    _disposables.addFromEvent(this._container, 'wheel', e => {
      e.stopPropagation();
    });

    _disposables.addFromEvent(this._container, 'pointerdown', e => {
      const ele = e.target as HTMLElement;
      // O botão de tirar não arrasta — senão um clique nele vira arraste de 0px e reordena.
      if (ele.closest('.tirar')) return;

      const draggable = ele.closest('.draggable');
      if (!draggable) return;

      const clone = this._clone;
      const indicatorLine = this._indicatorLine;
      clone.style.visibility = 'visible';

      const rect = draggable.getBoundingClientRect();
      const index = Number(draggable.getAttribute('index'));
      this._curIndex = index;
      let newIndex = -1;

      const containerRect = this._container.getBoundingClientRect();
      const start = containerRect.top + 8;
      const end = containerRect.bottom;

      const shiftX = e.clientX - rect.left;
      const shiftY = e.clientY - rect.top;
      function moveAt(x: number, y: number) {
        clone.style.left = x - containerRect.left - shiftX + 'px';
        clone.style.top = y - containerRect.top - shiftY + 'px';
      }
      function isInsideContainer(ev: PointerEvent) {
        return ev.clientY >= start && ev.clientY <= end;
      }
      moveAt(e.clientX, e.clientY);

      this._disposables.addFromEvent(document, 'pointermove', ev => {
        indicatorLine.style.visibility = 'visible';
        moveAt(ev.clientX, ev.clientY);
        if (isInsideContainer(ev)) {
          const relativeY = ev.pageY + this._container.scrollTop - start;
          let top = 0;
          if (relativeY < rect.height / 2) {
            newIndex = 0;
            top = 4;
          } else {
            newIndex = Math.ceil(
              (relativeY - rect.height / 2) / (rect.height + 10)
            );
            top = 7.5 + newIndex * rect.height + (newIndex - 0.5) * 4;
          }
          indicatorLine.style.top = top - this._container.scrollTop + 'px';
          return;
        }
        newIndex = -1;
      });

      this._disposables.addFromEvent(document, 'pointerup', () => {
        clone.style.visibility = 'hidden';
        indicatorLine.style.visibility = 'hidden';

        const passos = this._passos;
        if (
          this.frame &&
          newIndex >= 0 &&
          newIndex <= passos.length &&
          newIndex !== index &&
          newIndex !== index + 1
        ) {
          const movido = passos[index];
          if (movido) {
            // `newIndex` conta a lista COM o item; `moverPasso` espera o destino na lista SEM
            // ele. Descontar quando se arrasta para baixo é o que faz "soltar logo abaixo do
            // vizinho" cair onde o olho espera.
            const destino = newIndex > index ? newIndex - 1 : newIndex;
            moverPasso(this.crud, this.frame, movido.id, destino);
            this.edgeless.store.captureSync();
            this.requestUpdate();
          }
        }

        this._disposables.dispose();
        this._disposables = new DisposableGroup();
        this._bindEvent();
      });
    });
  }

  private _tirar(elementId: string) {
    if (!this.frame) return;
    desmarcarElemento(this.crud, this.frame, elementId);
    this.edgeless.store.captureSync();
    this.requestUpdate();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this._disposables.dispose();
  }

  override firstUpdated() {
    this._bindEvent();
  }

  override render() {
    const passos = this._passos;
    const emArraste = passos[this._curIndex];

    return html`
      <div class="container" @click=${(e: MouseEvent) => e.stopPropagation()}>
        ${
          passos.length === 0
            ? html`<div class="vazio">
                Nenhum elemento marcado neste quadro. Selecione uma forma, uma nota ou uma
                imagem dentro dele e use <b>Revelar por passo</b>.
              </div>`
            : repeat(
                passos,
                p => p.id,
                (p, index) => html`
                  <div class="item draggable" id=${p.id} index=${index}>
                    <div class="drag-indicator"></div>
                    <div class="numero">${p.numero}</div>
                    <div class="titulo">
                      ${rotuloDoElemento(this._gfx.getElementById(p.id))}
                    </div>
                    <button
                      class="tirar"
                      title="Tirar da apresentação"
                      @click=${(e: MouseEvent) => {
                        e.stopPropagation();
                        this._tirar(p.id);
                      }}
                    >
                      ✕
                    </button>
                  </div>
                `
              )
        }
        <div class="indicator-line"></div>
        <div class="clone item">
          ${
            emArraste
              ? html`<div class="drag-indicator"></div>
                  <div class="numero">${emArraste.numero}</div>
                  <div class="titulo">
                    ${rotuloDoElemento(this._gfx.getElementById(emArraste.id))}
                  </div>`
              : nothing
          }
        </div>
      </div>
    `;
  }

  @query('.clone')
  private accessor _clone!: HTMLDivElement;

  @query('.container')
  private accessor _container!: HTMLDivElement;

  @state()
  private accessor _curIndex = -1;

  @query('.indicator-line')
  private accessor _indicatorLine!: HTMLDivElement;

  @property({ attribute: false })
  accessor edgeless!: BlockComponent;

  @property({ attribute: false })
  accessor frame: FrameBlockModel | null = null;
}
